import * as React from 'react';

import { useIntl } from 'react-intl';

import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Input } from 'src/registry/ui/Input';
import SingleDatePicker from 'src/registry/ui/SingleDatePicker';
import { storage } from 'src/registry/utils/storage';
import { cn } from 'src/utils/shadcn';
import { useId } from 'react';
import { useMaskInput } from 'use-mask-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-regular-svg-icons';

type DataTableDateDropdownProps = {
    className?: string;
    afterDate: Date;
    beforeDate: Date;
    setValue: ({ afterDate, beforeDate }: { afterDate: Date; beforeDate: Date }) => void;
    setLabel?: (label: string) => void;
    enableTime?: boolean;
    inline?: boolean;
};
export default function DataTableDateDropdown({
    setValue,
    setLabel,
    enableTime,
    inline,
    beforeDate,
    afterDate,
    className,
}: DataTableDateDropdownProps) {
    const { formatMessage } = useIntl();
    const id = useId();

    const locale = storage.getLanguage();

    const formatDate = React.useCallback((date: Date) => {
        return date
            .toLocaleDateString(locale, {
                year: 'numeric',
                month: 'numeric',
                day: '2-digit',
            })
            .replace('г.', '');
    }, []);

    const aftertimeMask = useMaskInput({
        mask: 'datetime',
        options: {
            placeholder: '-',
            inputFormat: 'HH:mm',
            outputFormat: 'HH:mm',
            showMaskOnHover: false,
            clearIncomplete: true,
            insertMode: false,
        },
    });

    const beforeTimeMask = useMaskInput({
        mask: 'datetime',
        options: {
            placeholder: '-',
            inputFormat: 'HH:mm',
            outputFormat: 'HH:mm',
            showMaskOnHover: false,
            clearIncomplete: true,
            insertMode: false,
        },
    });

    const onAfterTimeChange = React.useCallback(
        ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
            if (!value || typeof value !== 'string') {
                return;
            }

            const time24hFormatMatch = value.match(/^(\d{2}):(\d{2})$/);

            if (!time24hFormatMatch) {
                return;
            }

            const hours = Number(time24hFormatMatch[1]);
            const minutes = Number(time24hFormatMatch[2]);

            if (
                Number.isNaN(hours) ||
                Number.isNaN(minutes) ||
                hours < 0 ||
                hours > 23 ||
                minutes < 0 ||
                minutes > 59
            ) {
                return;
            }

            const newAfterDate = new Date(afterDate);

            newAfterDate.setHours(hours, minutes, 0, 0);

            if (newAfterDate.getTime() > beforeDate.getTime()) {
                return;
            }

            setValue({
                beforeDate,
                afterDate: newAfterDate,
            });

            setLabel?.(`${formatDate(newAfterDate)} - ${formatDate(beforeDate)}`);
        },
        [afterDate, beforeDate, formatDate, setLabel, setValue],
    );

    const onBeforeTimeChange = React.useCallback(
        ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
            if (!value || typeof value !== 'string') {
                return;
            }

            const time24hFormatMatch = value.match(/^(\d{2}):(\d{2})$/);

            if (!time24hFormatMatch) {
                return;
            }

            const hours = Number(time24hFormatMatch[1]);
            const minutes = Number(time24hFormatMatch[2]);

            if (
                Number.isNaN(hours) ||
                Number.isNaN(minutes) ||
                hours < 0 ||
                hours > 23 ||
                minutes < 0 ||
                minutes > 59
            ) {
                return;
            }

            const newBeforeDate = new Date(beforeDate);

            newBeforeDate.setHours(hours, minutes, 0, 0);

            if (afterDate.getTime() > newBeforeDate.getTime()) {
                return;
            }

            setValue({
                afterDate,
                beforeDate: newBeforeDate,
            });

            setLabel?.(`${formatDate(afterDate)} - ${formatDate(newBeforeDate)}`);
        },
        [afterDate, beforeDate, formatDate, setLabel, setValue],
    );

    const formatTime = (date: Date) =>
        `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;

    return (
        <div className={cn('flex flex-row gap-1 px-2', className)}>
            <div className="flex w-full flex-col items-start justify-between gap-2 text-end">
                {!inline && <span className="w-full text-left text-sm">{formatMessage(tableMessages.from)}</span>}
                <SingleDatePicker
                    className="w-full"
                    buttonValue={formatDate(afterDate)}
                    value={new Date(afterDate.getTime() - afterDate.getTimezoneOffset() * 60 * 1000)}
                    onChange={(date) => {
                        if (!date) return;

                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                        utcDate.setHours(afterDate.getHours(), afterDate.getMinutes());

                        let newBeforeDate = beforeDate;
                        if (utcDate.getTime() > beforeDate.getTime()) {
                            newBeforeDate = new Date(utcDate);
                        }

                        setValue({ afterDate: utcDate, beforeDate: newBeforeDate });
                        if (!setLabel) return;
                        setLabel(`${formatDate(utcDate)} - ${formatDate(beforeDate)}`);
                    }}
                />
                {enableTime && (
                    <div className="relative w-fit">
                        <FontAwesomeIcon
                            icon={faClock}
                            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-2 -translate-y-1/2"
                        />
                        <Input
                            id={id}
                            type="text"
                            placeholder="--:--"
                            defaultValue={formatTime(afterDate)}
                            onChange={onAfterTimeChange}
                            ref={aftertimeMask}
                            className="w-24 text-right text-sm"
                        />
                    </div>
                )}
            </div>

            {inline ? (
                <span className="flex items-center px-2 text-sm">{formatMessage(tableMessages.to)}</span>
            ) : (
                <div className="flex flex-col items-center justify-center">
                    <span className="text-sm" contentEditable={true} />
                </div>
            )}

            <div className="flex w-full flex-col items-start gap-2 text-start align-baseline">
                {!inline && <span className="px-0.5 text-left text-sm">{formatMessage(tableMessages.to)}</span>}
                <SingleDatePicker
                    className="w-full"
                    buttonValue={formatDate(beforeDate)}
                    value={new Date(beforeDate.getTime() - beforeDate.getTimezoneOffset() * 60 * 1000)}
                    onChange={(date) => {
                        if (!date) return;

                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                        utcDate.setHours(beforeDate.getHours(), beforeDate.getMinutes());

                        let newAfterDate = afterDate;
                        if (afterDate.getTime() > utcDate.getTime()) {
                            newAfterDate = new Date(utcDate);
                        }

                        setValue({ afterDate: newAfterDate, beforeDate: utcDate });
                        if (!setLabel) return;
                        setLabel(`${formatDate(afterDate)} - ${formatDate(utcDate)}`);
                    }}
                />
                {enableTime && (
                    <div className="relative w-fit">
                        <FontAwesomeIcon
                            icon={faClock}
                            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-2 -translate-y-1/2"
                        />
                        <Input
                            id={id}
                            type="text"
                            placeholder="--:--"
                            defaultValue={formatTime(beforeDate)}
                            onChange={onBeforeTimeChange}
                            ref={beforeTimeMask}
                            className="w-24 text-right text-sm"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
